import { type Address, type AddressRegion, type AddressStored } from "@jti/fe-common";

export type CurrentAddress = AddressStored | Address;

type GetEmptyAddressArgs<TCurrentAddress extends CurrentAddress> = {
  overrides: {
    countryName: string;
    countryCode: string;
    region?: AddressRegion;
    regionCode?: string;
  };
  currentAddress?: TCurrentAddress;
};

const BASE_EMPTY_ADDRESS_FIELDS: Omit<AddressStored, "countryName"> & { countryName: string } = {
  countryCode: "",
  countryName: "",
  id: "",
  firstname: "",
  lastname: "",
  streetName: "",
  streetNumber: "",
  streetAdditional: "",
  city: "",
  postcode: "",
  telephone: "",
  isSubscription: false,
};

const EMPTY_ADDRESS_FIELDS: Required<AddressStored> = {
  ...BASE_EMPTY_ADDRESS_FIELDS,
  pickupInformation: {},
  provinceName: "",
  districtName: "",
  regionCode: "",
  region: { region_code: "", region: "", region_id: 0 },
  neighborhood: "",
  district: "",
  buildingNumber: "",
  premise: "",
  landmark: "",
  shortAddress: "",
  company: "",
  isDefaultBilling: false,
  isDefaultShipping: false,
  isPickupAddress: false,
};

const getExtraFieldsFromCurrentAddress = <TAddress extends CurrentAddress>(
  currentAddress: TAddress
): Partial<TAddress> =>
  Object.fromEntries(
    Object.entries(EMPTY_ADDRESS_FIELDS).filter(([key]) => key in currentAddress)
  ) as Partial<TAddress>;

const sanitizeOverrides = <TCurrentAddress extends CurrentAddress>(
  overrides: GetEmptyAddressArgs<TCurrentAddress>["overrides"]
): Partial<GetEmptyAddressArgs<TCurrentAddress>["overrides"]> => {
  if (!overrides) return {};

  return Object.fromEntries(Object.entries(overrides).filter(([_, value]) => value !== undefined));
};

export const getEmptyAddress = <TCurrentAddress extends CurrentAddress>({
  overrides,
  currentAddress,
}: GetEmptyAddressArgs<TCurrentAddress>): TCurrentAddress => {
  const sanitizedOverrides = sanitizeOverrides(overrides);

  if (!currentAddress) {
    return {
      ...BASE_EMPTY_ADDRESS_FIELDS,
      ...sanitizedOverrides,
    } as TCurrentAddress;
  }

  const extraFields = getExtraFieldsFromCurrentAddress(currentAddress);

  return {
    ...extraFields,
    ...sanitizedOverrides,
  } as TCurrentAddress;
};
